import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { getNoteSnapshot } from "../../../../../../../services/notes";

/**
 * Processes the API response into structured data for components
 * @param {Array} body - The response body array of note groups
 * @returns {Object} Processed snapshot data
 */
const processSnapshotResponse = (body) => {
  const snapshot = {
    summary: null,
    nba: [],
    opportunities: [],
    recommendations: [],
    riskFactors: [],
    tags: {},
    traits: [],
  };

  if (!Array.isArray(body)) return snapshot;

  for (const group of body) {
    switch (group.note_type) {
      case "SUMMARY":
        snapshot.summary = group.notes[0]?.note_content || null;
        break;

      case "NBA":
        snapshot.nba = group.notes.map((n) => ({
          id: n.note_uuid,
          content: n.note_content,
          createdAt: n.created_at,
        }));
        break;

      case "OPPORTUNITIES":
        snapshot.opportunities = group.notes.flatMap((n) => n.metadata || []);
        break;

      case "RECOMMENDATIONS":
        snapshot.recommendations = group.notes.flatMap((n) => n.metadata || []);
        break;

      case "RISK_FACTORS":
        snapshot.riskFactors = group.notes.flatMap((n) => n.metadata || []);
        break;

      case "TAG":
        for (const note of group.notes) {
          const category = note.note_content;
          snapshot.tags[category] = (note.metadata || []).map((tag) => ({
            tag: tag.tag,
            ttlDays: tag.ttl_days,
            expiresAt: tag.expires_at,
          }));
        }
        break;

      case "TRAIT":
        snapshot.traits = group.notes.map((n) => ({
          name: n.note_content,
          currentValue: n.metadata?.current_value,
          confidencePct: n.metadata?.confidence_pct,
          ttlDays: n.metadata?.ttl_days,
          expiresAt: n.metadata?.expires_at,
          guidance: n.metadata?.guidance || [],
        }));
        break;

      default:
        break;
    }
  }

  return snapshot;
};

/**
 * Hook to fetch and manage note snapshot data for a contact
 */
export const useNoteSnapshot = () => {
  const { contactId } = useParams();

  const [state, setState] = useState({
    data: null,
    loading: false,
    error: null,
  });

  const hasAttemptedLoadRef = useRef(false);
  const lastContactIdRef = useRef(null);

  const fetchSnapshot = useCallback(async () => {
    if (!contactId) return;

    hasAttemptedLoadRef.current = true;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await getNoteSnapshot(contactId);

      if (response?.statusCode !== 200) {
        throw new Error(
          response?.msgCode?.[0]?.description || "Failed to fetch snapshot"
        );
      }

      const processedData = processSnapshotResponse(response.body || []);

      setState({
        data: processedData,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error("Error fetching note snapshot:", err);
      setState({
        data: null,
        loading: false,
        error: err,
      });
      hasAttemptedLoadRef.current = false;
    }
  }, [contactId]);

  // Reset state when contact changes
  useEffect(() => {
    if (
      contactId &&
      lastContactIdRef.current !== null &&
      lastContactIdRef.current !== contactId
    ) {
      setState({ data: null, loading: false, error: null });
      hasAttemptedLoadRef.current = false;
    }
    lastContactIdRef.current = contactId;
  }, [contactId]);

  // Initial fetch when contactId is available
  useEffect(() => {
    if (!contactId) return;
    if (state.data) {
      hasAttemptedLoadRef.current = true;
      return;
    }
    if (hasAttemptedLoadRef.current) return;

    fetchSnapshot();
  }, [contactId, state.data, fetchSnapshot]);

  const handleRefresh = useCallback(() => {
    hasAttemptedLoadRef.current = false;
    fetchSnapshot();
  }, [fetchSnapshot]);

  // Computed values for each tab
  const hasData = useMemo(() => {
    if (!state.data) return false;
    const d = state.data;
    return !!(
      d.summary ||
      d.nba.length > 0 ||
      d.opportunities.length > 0 ||
      d.recommendations.length > 0 ||
      d.riskFactors.length > 0 ||
      Object.keys(d.tags).length > 0 ||
      d.traits.length > 0
    );
  }, [state.data]);

  const hasInsightData = useMemo(() => {
    if (!state.data) return false;
    const d = state.data;
    return !!(
      d.summary ||
      d.opportunities.length > 0 ||
      d.recommendations.length > 0 ||
      d.riskFactors.length > 0
    );
  }, [state.data]);

  const hasTagsData = useMemo(() => {
    if (!state.data) return false;
    return Object.keys(state.data.tags).length > 0;
  }, [state.data]);

  const hasActionsData = useMemo(() => {
    if (!state.data) return false;
    return state.data.traits.length > 0 || state.data.nba.length > 0;
  }, [state.data]);

  return {
    // Raw data
    data: state.data,
    loading: state.loading,
    error: state.error,

    // Processed sections
    summary: state.data?.summary || null,
    nba: state.data?.nba || [],
    opportunities: state.data?.opportunities || [],
    recommendations: state.data?.recommendations || [],
    riskFactors: state.data?.riskFactors || [],
    tags: state.data?.tags || {},
    traits: state.data?.traits || [],

    // State flags
    hasData,
    hasInsightData,
    hasTagsData,
    hasActionsData,

    // Actions
    handleRefresh,
  };
};
